// src/components/merchant/MyDeals.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiEdit, FiTrash2, FiEye, FiPlus, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import { getDealImageUrl } from '../../utils/dealImageHelper';

const fmtCurrency = (value) =>
  typeof value === 'number'
    ? value.toLocaleString('fr-FR') + ' MAD'
    : value
    ? `${value} MAD`
    : '0 MAD';

const MyDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort();
      } catch (e) {}
      clearTimeout(debounceTimer.current);
    };
  }, []);

  const extractListFromResponse = (res) => {
    const obj = res?.data ?? res ?? null;
    if (!obj) return [];
    const payload = obj?.data ?? obj;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.deals)) return payload.deals;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const tryGet = async (url, signal) => {
    try {
      const res = await API.get(url, { signal });
      return { ok: true, data: extractListFromResponse(res) };
    } catch (err) {
      const isAbort =
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED' ||
        err?.message === 'canceled';
      if (isAbort) throw err;
      return { ok: false, err };
    }
  };

  const fetchDeals = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      try {
        abortRef.current?.abort();
      } catch (e) {}
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;

      const endpoints = ['/merchant/me/deals', '/merchant/deals', '/deals'];

      let final = [];
      let lastError = null;

      for (const ep of endpoints) {
        try {
          const r = await tryGet(ep, signal);
          if (r.ok) {
            final = Array.isArray(r.data) ? r.data : [];
            break;
          } else {
            lastError = r.err;
            continue;
          }
        } catch (abortErr) {
          throw abortErr;
        }
      }

      if (mountedRef.current) {
        setDeals(final);
        if (final.length === 0 && lastError) {
          const status = lastError?.response?.status;
          if (status && status !== 404)
            setError('Impossible de récupérer les deals (erreur serveur)');
        }
      }
    } catch (err) {
      if (
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED' ||
        err?.message === 'canceled'
      )
        return;
      console.error('fetchDeals error', err);
      if (mountedRef.current) {
        setError('Impossible de récupérer les deals');
        toast.error('Erreur lors du chargement des deals');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce deal ? Cette action est irréversible.'))
      return;
    setDeletingId(id);

    const prev = deals;
    setDeals((prevList) => prevList.filter((d) => (d._id || d.id) !== id));

    try {
      await API.delete(`/deals/id/${id}`);
      toast.success('Deal supprimé');
    } catch (err) {
      console.error('delete deal error', err);
      toast.error(
        err?.response?.data?.message || 'Erreur lors de la suppression'
      );
      setDeals(prev);
      setTimeout(() => {
        if (mountedRef.current) fetchDeals();
      }, 500);
    } finally {
      if (mountedRef.current) setDeletingId(null);
    }
  };

  const [filteredDeals, setFilteredDeals] = useState([]);
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const q = (searchTerm || '').trim().toLowerCase();
      if (!q) {
        setFilteredDeals(deals);
        return;
      }
      setFilteredDeals(
        deals.filter((deal) => {
          const title = (deal.title || deal.name || '')
            .toString()
            .toLowerCase();
          const category = (deal.category?.name || deal.category || '')
            .toString()
            .toLowerCase();
          return title.includes(q) || category.includes(q);
        })
      );
    }, 220);
    return () => clearTimeout(debounceTimer.current);
  }, [searchTerm, deals]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const toNumber = (...vals) => {
    for (const v of vals) {
      if (v === null || v === undefined) continue;
      if (typeof v === 'number' && Number.isFinite(v)) return v;

      const cleaned = String(v)
        .replace(/[^\d,.,-]/g, '')
        .replace(',', '.');

      const n = parseFloat(cleaned);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Mes Deals</h1>
        <Link
          to="/merchant/deals/new"
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FiPlus className="mr-2" /> Nouveau Deal
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un deal..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredDeals.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Image
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Deal
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Prix
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Ventes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDeals.map((deal) => {
                const id = deal._id || deal.id;
                const categoryName =
                  deal.category?.name || deal.category || '—';

                const price = toNumber(
                  deal.discountedPrice,
                  deal.price,
                  deal.currentPrice,
                  deal.promoPrice
                );

                const originalPrice = toNumber(
                  deal.originalPrice,
                  deal.basePrice,
                  deal.oldPrice,
                  deal.price
                );

                // ✅ Utiliser getDealImageUrl pour obtenir l'URL de l'image
                const imgUrl = getDealImageUrl(deal, 0, '');

                return (
                  <tr key={id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 w-24">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={deal.title || 'image'}
                          className="w-20 h-12 object-cover rounded"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/placeholder.jpg';
                          }}
                        />
                      ) : (
                        <div className="w-20 h-12 bg-gray-100 flex items-center justify-center rounded text-xs text-gray-400">
                          No img
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900 truncate">
                        {deal.title || deal.name || 'Sans titre'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {categoryName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold">
                        {fmtCurrency(price)}
                      </div>
                      <div className="text-xs text-gray-500 line-through">
                        {fmtCurrency(originalPrice)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          deal.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {deal.status || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {deal.soldCount ?? deal.quantitySold ?? 0} /{' '}
                      {deal.quantity ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/deals/${deal.slug || id}`}
                          className="text-blue-600 hover:text-blue-900"
                          aria-label="Voir"
                        >
                          <FiEye size={18} />
                        </Link>
                        <Link
                          to={`/merchant/deals/${id}/edit`}
                          state={{ deal }}
                          className="text-green-600 hover:text-green-900"
                          aria-label="Éditer"
                        >
                          <FiEdit size={18} />
                        </Link>
                        <button
                          onClick={() => handleDelete(id)}
                          className="text-red-600 hover:text-red-900"
                          disabled={deletingId === id}
                          title="Supprimer"
                          aria-disabled={deletingId === id}
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <FiPlus className="mx-auto text-4xl text-gray-400 mb-2" />
            <p className="text-gray-600">Aucun deal créé</p>
            <Link
              to="/merchant/deals/new"
              className="text-primary-600 hover:underline mt-2 inline-block"
            >
              Créer votre premier deal
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyDeals;